import { resolveCmmdRuntimeSpriteName } from "@cmmd-forge/contracts";

type CloudRuntimeTerminalFailureCode =
  | "runtime_boot_failed"
  | "runtime_recycle_failed"
  | "runtime_volume_missing"
  | "runtime_volume_full";

export interface CloudRuntimeTerminalFailureResidual {
  readonly stage: string | null;
  readonly runtimeId: string | null;
  readonly environmentId: string | null;
  readonly status: string | null;
  readonly spriteName: string | null;
  readonly readinessPhase: string | null;
  readonly retryable: boolean | null;
  readonly ensureDetail: string | null;
}

const SPRITE_MISSING_SIGNAL =
  /\b(404|not found|missing|unreachable|orphan|no such sprite|sprite_not_found|sprite missing)\b/i;

export interface CloudRuntimeConnectFailureAttachment {
  readonly stage?: string;
  /** Progress or ensure summary without CMMD's raw guest `error` string. */
  readonly ensureDetail?: string;
}

const TERMINAL_WHY_BY_CODE: Readonly<Record<CloudRuntimeTerminalFailureCode, string>> = {
  runtime_boot_failed:
    "CMMD hit the boot deadline: repeated wake attempts did not show guest life in time.",
  runtime_recycle_failed:
    "Recycling this Environment in place failed before the guest could finish repair.",
  runtime_volume_missing:
    "The workspace storage volume is missing, so CMMD cannot mount this Environment.",
  runtime_volume_full:
    "The workspace storage volume is full, so CMMD cannot write to this Environment.",
};

export const TERMINAL_RUNTIME_FOLLOW_UP =
  "Recycle it from Environments, or contact support with the error code below.";

function readNonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

function formatRuntimeEnsureDetail(runtime: {
  readonly spriteName?: string | null;
  readonly flyMachineId?: string | null;
  readonly flyVolumeId?: string | null;
  readonly readinessPhase?: string | null;
}): string {
  const spriteName = resolveCmmdRuntimeSpriteName(runtime);
  const parts = [
    spriteName ? `Sprite ${spriteName}` : null,
    runtime.flyVolumeId ? `volume ${runtime.flyVolumeId}` : null,
    runtime.readinessPhase ? runtime.readinessPhase : null,
  ].filter((part): part is string => part !== null);
  return parts.length > 0 ? parts.join(" · ") : "CMMD returned an ensured runtime.";
}

const SPRITE_MISSING_WHY =
  "CMMD still points at a Sprite the substrate cannot reach (missing, wrong org, or deleted). Recycle from Environments to attach a new Sprite.";

function residualWireText(residual: CloudRuntimeTerminalFailureResidual | null): string {
  if (!residual) return "";
  return [residual.ensureDetail, residual.readinessPhase, residual.status]
    .filter((part): part is string => typeof part === "string" && part.length > 0)
    .join(" ");
}

function inferSpriteMissingWhy(
  code: string,
  residual: CloudRuntimeTerminalFailureResidual | null,
): string | null {
  if (code !== "runtime_boot_failed" || residual === null) return null;
  const wireText = residualWireText(residual);
  if (SPRITE_MISSING_SIGNAL.test(wireText)) return SPRITE_MISSING_WHY;
  return null;
}

export function terminalRuntimeWhyCopy(
  code: string,
  residual: CloudRuntimeTerminalFailureResidual | null = null,
): string {
  const spriteMissingWhy = inferSpriteMissingWhy(code, residual);
  if (spriteMissingWhy !== null) return spriteMissingWhy;

  if (Object.hasOwn(TERMINAL_WHY_BY_CODE, code)) {
    return TERMINAL_WHY_BY_CODE[code as CloudRuntimeTerminalFailureCode];
  }
  return "This workspace is in a terminal state CMMD will not retry automatically.";
}

export function readCloudRuntimeFailureResidual(
  body: unknown,
  attachment: CloudRuntimeConnectFailureAttachment = {},
): CloudRuntimeTerminalFailureResidual | null {
  const runtime =
    body && typeof body === "object" && (body as { readonly runtime?: unknown }).runtime;
  const runtimeRecord =
    runtime && typeof runtime === "object"
      ? (runtime as {
          readonly runtimeId?: unknown;
          readonly environmentId?: unknown;
          readonly status?: unknown;
          readonly spriteName?: unknown;
          readonly flyMachineId?: unknown;
          readonly flyVolumeId?: unknown;
          readonly readinessPhase?: unknown;
        })
      : null;

  const retryableRaw =
    body && typeof body === "object"
      ? (body as { readonly retryable?: unknown }).retryable
      : undefined;
  const retryable = typeof retryableRaw === "boolean" ? retryableRaw : null;

  const ensureDetailFromRuntime =
    runtimeRecord !== null
      ? formatRuntimeEnsureDetail({
          spriteName: readNonEmptyString(runtimeRecord.spriteName),
          flyMachineId: readNonEmptyString(runtimeRecord.flyMachineId),
          flyVolumeId: readNonEmptyString(runtimeRecord.flyVolumeId),
          readinessPhase: readNonEmptyString(runtimeRecord.readinessPhase),
        })
      : null;
  const ensureDetail =
    readNonEmptyString(attachment.ensureDetail) ??
    (ensureDetailFromRuntime !== "CMMD returned an ensured runtime."
      ? ensureDetailFromRuntime
      : null);

  const residual: CloudRuntimeTerminalFailureResidual = {
    stage: readNonEmptyString(attachment.stage),
    runtimeId: runtimeRecord ? readNonEmptyString(runtimeRecord.runtimeId) : null,
    environmentId: runtimeRecord ? readNonEmptyString(runtimeRecord.environmentId) : null,
    status: runtimeRecord ? readNonEmptyString(runtimeRecord.status) : null,
    spriteName: runtimeRecord ? readNonEmptyString(runtimeRecord.spriteName) : null,
    readinessPhase: runtimeRecord ? readNonEmptyString(runtimeRecord.readinessPhase) : null,
    retryable,
    ensureDetail,
  };

  const hasValue = Object.values(residual).some((value) => value !== null);
  return hasValue ? residual : null;
}

export function formatTerminalFailureResidualForDisplay(
  residual: CloudRuntimeTerminalFailureResidual | null | undefined,
): string | null {
  if (!residual) return null;
  const parts: string[] = [];
  const push = (label: string, value: string | boolean | null) => {
    if (value === null || value === "") return;
    parts.push(`${label}=${String(value)}`);
  };
  push("stage", residual.stage);
  push("runtimeId", residual.runtimeId);
  push("environmentId", residual.environmentId);
  push("status", residual.status);
  push("spriteName", residual.spriteName);
  push("readinessPhase", residual.readinessPhase);
  push("retryable", residual.retryable);
  push("ensureDetail", residual.ensureDetail);
  return parts.length > 0 ? parts.join(" · ") : null;
}
